import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Container,
  Group,
  NumberInput,
  Paper,
  Progress,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  UnstyledButton,
  type CSSObject,
  type MantineTheme,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { PageHeader } from '../common/PageHeader';
import { PaginationBar } from '../common/PaginationBar';
import { AppTooltip } from '../common/AppTooltip';
import { GivebackRiskIndicator } from './GivebackRiskCard';
import { financialsApi } from '../../api/financials';
import {
  FinancialAwardWindow,
  FinancialMonthRow,
  FinancialMonthSubmission,
  FinancialSpendTargets,
} from '../../types/financials';
import { useGroupQuery } from '../../hooks/useGroupQuery';
import { formatTimestampToLocal } from '../../utils/dateUtils';
import {
  showApiErrorNotification,
  showSuccessNotification,
} from '../../utils/notifications';

type EditableField = 'federal' | 'match';

const PAGE_SIZE = 12;

const currencyFormatter = (value: string) => {
  if (!value) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value));
};

const currencyParser = (value: string) => {
  if (!value) return '';
  return value.replace(/[^0-9.-]+/g, '');
};

const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

function monthKey(year: number, month: number): string {
  return `${year}-${month}`;
}

function comparePeriodDesc(a: { year: number; month: number }, b: { year: number; month: number }): number {
  if (a.year !== b.year) return b.year - a.year;
  return b.month - a.month;
}

function pageForCurrentWork(rows: FinancialMonthRow[]): number {
  const idx = rows.findIndex((row) => row.status !== 'upcoming');
  if (idx < 0) return 1;
  return Math.floor(idx / PAGE_SIZE) + 1;
}

function compareSubmissionPeriod(
  a: FinancialMonthSubmission,
  b: FinancialMonthSubmission
): number {
  if (a.year !== b.year) return a.year - b.year;
  return a.month - b.month;
}

function latestFieldValue(
  submissions: Record<string, FinancialMonthSubmission>,
  field: 'federalSpendToDate' | 'matchSpendToDate'
): { value: number; submission: FinancialMonthSubmission } | null {
  const list = Object.values(submissions)
    .filter((entry) => entry[field] !== null)
    .sort(compareSubmissionPeriod);
  const submission = list.at(-1);
  if (!submission) return null;
  const value = submission[field];
  if (value === null) return null;
  return { value, submission };
}

function hasReportedSpend(submission: FinancialMonthSubmission | undefined): boolean {
  if (!submission) return false;
  return submission.federalSpendToDate !== null || submission.matchSpendToDate !== null;
}

/** Ledger months begin July 2026 and extend through next calendar month. */
const REPORTING_PERIOD_START = dayjs('2026-07-01').startOf('month');

function buildAllTimeMonthRows(
  submissions: Record<string, FinancialMonthSubmission>
): FinancialMonthRow[] {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month() + 1;
  const start = REPORTING_PERIOD_START;
  const end = now.add(1, 'month').startOf('month');
  const rows: FinancialMonthRow[] = [];

  // If "now + 1 month" is still before the reporting start, show no periods yet.
  if (end.isBefore(start, 'month')) {
    return rows;
  }

  let cursor = start;
  while (cursor.isBefore(end) || cursor.isSame(end, 'month')) {
    const year = cursor.year();
    const month = cursor.month() + 1;
    const key = monthKey(year, month);
    const submission = submissions[key];
    const isFuture =
      year > currentYear || (year === currentYear && month > currentMonth);

    let status: FinancialMonthRow['status'];
    if (isFuture) {
      status = 'upcoming';
    } else if (hasReportedSpend(submission)) {
      status = 'reported';
    } else {
      status = 'not_reported';
    }

    rows.push({
      year,
      month,
      label: cursor.format('MMMM YYYY'),
      status,
      federalSpendToDate: submission?.federalSpendToDate ?? null,
      matchSpendToDate: submission?.matchSpendToDate ?? null,
      updatedAt: submission?.updatedAt ?? null,
      updatedBy: submission?.updatedBy ?? null,
    });

    cursor = cursor.add(1, 'month');
  }

  return rows.sort(comparePeriodDesc);
}

function pctOfTarget(actual: number, target: number): number {
  if (target <= 0) return 0;
  return Math.round((actual / target) * 100);
}

function SpendMetricTile({
  eyebrow,
  label,
  value,
  asOf,
  emptyLabel,
  target,
}: {
  eyebrow: string;
  label: string;
  value: number | null;
  asOf: string | null;
  emptyLabel: string;
  target?: number;
}) {
  const hasValue = value !== null;
  const pct = target !== undefined && hasValue ? pctOfTarget(value, target) : null;
  const barPct = pct === null ? 0 : Math.min(pct, 100);
  const overTarget = pct !== null && value !== null && target !== undefined && value > target;

  return (
    <Box
      p="md"
      sx={(theme) => ({
        borderRadius: theme.radius.md,
        backgroundColor:
          theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        border: `1px solid ${
          theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
        }`,
        height: '100%',
      })}
    >
      <Stack spacing="sm">
        <div>
          <Text size="xs" color="dimmed" weight={700} tt="uppercase" sx={{ letterSpacing: '0.04em' }}>
            {eyebrow}
          </Text>
          <Text size="sm" weight={600} mt={2}>
            {label}
          </Text>
        </div>

        {hasValue ? (
          <Text
            weight={700}
            sx={{
              fontSize: 28,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCurrency(value)}
          </Text>
        ) : (
          <Text color="dimmed" weight={600} sx={{ fontSize: 28, lineHeight: 1.15 }}>
            —
          </Text>
        )}

        {hasValue && asOf ? (
          <Text size="xs" color="dimmed">
            As of {asOf}
          </Text>
        ) : !hasValue ? (
          <Text size="xs" color="dimmed">
            {emptyLabel}
          </Text>
        ) : null}

        {target !== undefined ? (
          <Stack spacing={6} mt={4}>
            <Progress
              value={hasValue ? barPct : 0}
              size="md"
              radius="xl"
              color={overTarget ? 'orange' : pct !== null && pct >= 100 ? 'green' : 'blue'}
              aria-label={`${label} target: ${pct ?? 0}%`}
            />
            <Group position="apart" noWrap spacing="xs">
              <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {hasValue ? `${pct}% of budget` : '—'}
              </Text>
              <Text size="xs" color="dimmed" ta="right">
                {overTarget && hasValue
                  ? `${formatCurrency(value - target)} over budget`
                  : formatCurrency(target)}
              </Text>
            </Group>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}

type FinancialsPaperSx = (theme: MantineTheme) => CSSObject;

function FinancialsPageSkeleton({ paperSx }: { paperSx: FinancialsPaperSx }) {
  return (
    <Stack spacing="md" mb="xl">
      <Paper p="md" shadow="md" radius="md" sx={paperSx}>
        <Stack spacing="md">
          <Stack spacing="sm">
            <div>
              <Skeleton height={28} width={180} mb={8} />
              <Skeleton height={16} width={260} />
            </div>
            <Stack spacing={6}>
              <Group position="apart" noWrap>
                <Skeleton height={12} width={90} />
                <Skeleton height={12} width={110} />
                <Skeleton height={12} width={90} />
              </Group>
              <Skeleton height={6} radius="xl" />
            </Stack>
          </Stack>

          <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
            {[0, 1].map((key) => (
              <Box
                key={key}
                p="md"
                sx={(theme) => ({
                  borderRadius: theme.radius.md,
                  backgroundColor:
                    theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                  border: `1px solid ${
                    theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
                  }`,
                })}
              >
                <Stack spacing="sm">
                  <Skeleton height={12} width={70} />
                  <Skeleton height={16} width={120} />
                  <Skeleton height={28} width={140} />
                  <Skeleton height={12} width={100} />
                  <Skeleton height={8} radius="xl" mt={4} />
                  <Group position="apart">
                    <Skeleton height={14} width={40} />
                    <Skeleton height={12} width={120} />
                  </Group>
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        </Stack>
      </Paper>

      <Paper
        p="md"
        shadow="md"
        radius="md"
        sx={(theme) => ({
          ...paperSx(theme),
          maxWidth: 800,
        })}
      >
        <Group spacing="md" grow>
          <Stack spacing={8}>
            <Skeleton height={14} width={100} />
            <Skeleton height={36} radius="sm" />
          </Stack>
          <Stack spacing={8}>
            <Skeleton height={14} width={110} />
            <Skeleton height={36} radius="sm" />
          </Stack>
        </Group>
      </Paper>

      <Paper shadow="md" radius="md" sx={paperSx}>
        <Box p="md">
          <Skeleton height={28} width={160} mb={8} />
          <Skeleton height={16} width={220} />
        </Box>
        <Box px="md" pb="md">
          <Stack spacing="sm">
            <Group position="apart" noWrap>
              <Skeleton height={12} width="18%" />
              <Skeleton height={12} width="20%" />
              <Skeleton height={12} width="20%" />
              <Skeleton height={12} width="30%" />
            </Group>
            {Array.from({ length: 8 }).map((_, index) => (
              <Group key={index} position="apart" noWrap>
                <Skeleton height={16} width="18%" />
                <Skeleton height={16} width="20%" />
                <Skeleton height={16} width="20%" />
                <Skeleton height={16} width="30%" />
              </Group>
            ))}
          </Stack>
        </Box>
      </Paper>
    </Stack>
  );
}

export type FinancialsPageProps = {
  /** Load/save for this subawardee (admin). Uses default selected group when omitted. */
  groupScope?: string;
  /** Skip page Container + PageHeader when nested in an admin shell. */
  embedded?: boolean;
};

const FINANCIALS_SUBTITLE =
  'Track cumulative federal and match spend against award targets, and report monthly to-date totals.';

export function FinancialsPage({ groupScope, embedded = false }: FinancialsPageProps = {}) {
  const queryClient = useQueryClient();

  const groupQuery = useGroupQuery(['financials'], financialsApi.getFinancials, {
    enabled: !groupScope,
  });

  const scopedQuery = useQuery({
    queryKey: ['financials', groupScope],
    queryFn: () => financialsApi.getFinancialsForSubawardee(groupScope!),
    enabled: Boolean(groupScope),
    staleTime: 5 * 60 * 1000,
  });

  const groupSummaryQuery = useGroupQuery(
    ['financials-summary'],
    () => financialsApi.getFinancialsSummary(),
    { enabled: !groupScope, refetchOnMount: 'always' }
  );

  const scopedSummaryQuery = useQuery({
    queryKey: ['financials-summary', groupScope],
    queryFn: () => financialsApi.getFinancialsSummary(groupScope!),
    enabled: Boolean(groupScope),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });

  const { data: financials, isLoading, error } = groupScope ? scopedQuery : groupQuery;
  const givebackRisk =
    (groupScope ? scopedSummaryQuery.data : groupSummaryQuery.data)?.givebackRisk ?? null;

  const upsertMutation = useMutation({
    mutationFn: ({
      year,
      month,
      federalSpendToDate,
      matchSpendToDate,
    }: {
      year: number;
      month: number;
      federalSpendToDate: number | null;
      matchSpendToDate: number | null;
    }) =>
      financialsApi.upsertMonthSpend(
        year,
        month,
        {
          federalSpendToDate,
          matchSpendToDate,
        },
        groupScope
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupScope ? ['financials', groupScope] : ['financials'],
      });
      queryClient.invalidateQueries({
        queryKey: groupScope ? ['financials-summary', groupScope] : ['financials-summary'],
      });
      if (groupScope) {
        queryClient.invalidateQueries({ queryKey: ['component-project-financials'] });
      }
    },
    onError: (err) => {
      showApiErrorNotification(err, 'Failed to save financials');
    },
  });

  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [page, setPage] = useState(1);
  const initializedPageRef = useRef(false);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [focusField, setFocusField] = useState<EditableField>('federal');
  const [federalSpend, setFederalSpend] = useState<number | ''>('');
  const [matchSpend, setMatchSpend] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const blurTimeoutRef = useRef<number | null>(null);
  const editDraftRef = useRef({
    editingKey: null as string | null,
    federalSpend: '' as number | '',
    matchSpend: '' as number | '',
  });

  const awardWindow: FinancialAwardWindow = financials?.awardWindow ?? {
    startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('year').format('YYYY-MM-DD'),
  };
  const targets: FinancialSpendTargets = financials?.spendTargets ?? {
    federalTarget: 0,
    matchTarget: 0,
  };

  const submissions = useMemo(() => {
    const entries = financials?.submissions ?? [];
    return Object.fromEntries(
      entries.map((entry) => [monthKey(entry.year, entry.month), entry])
    ) as Record<string, FinancialMonthSubmission>;
  }, [financials?.submissions]);

  useEffect(() => {
    editDraftRef.current = { editingKey, federalSpend, matchSpend };
  }, [editingKey, federalSpend, matchSpend]);

  useEffect(() => {
    if (!editingKey) return;

    const labelPart = focusField === 'federal' ? 'Federal' : 'Match';
    const frame = window.requestAnimationFrame(() => {
      const input = document.querySelector(
        `tr[data-row-key="${editingKey}"] input[aria-label*="${labelPart}"]`
      ) as HTMLInputElement | null;
      input?.focus();
      input?.select();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [editingKey, focusField]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current !== null) {
        window.clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    initializedPageRef.current = false;
  }, [financials?.subawardeeId]);

  const allMonthRows = useMemo(
    () => buildAllTimeMonthRows(submissions),
    [submissions]
  );

  const yearOptions = useMemo(() => {
    const years = Array.from(new Set(allMonthRows.map((row) => row.year))).sort(
      (a, b) => b - a
    );
    return [
      { value: 'all', label: 'All years' },
      ...years.map((y) => ({ value: String(y), label: String(y) })),
    ];
  }, [allMonthRows]);

  const statusOptions = [
    { value: 'all', label: 'All months' },
    { value: 'reported', label: 'Filled' },
    { value: 'not_reported', label: 'Blank' },
    { value: 'upcoming', label: 'Upcoming' },
  ];

  const filteredRows = useMemo(() => {
    let rows = allMonthRows;
    if (selectedYear !== 'all') {
      const year = Number(selectedYear);
      rows = rows.filter((row) => row.year === year);
    }
    if (statusFilter === 'all') return rows;
    return rows.filter((row) => row.status === statusFilter);
  }, [allMonthRows, selectedYear, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    if (!initializedPageRef.current && filteredRows.length > 0) {
      initializedPageRef.current = true;
      setPage(pageForCurrentWork(filteredRows));
      return;
    }
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [filteredRows, page, safePage]);

  const latestFederal = useMemo(
    () => latestFieldValue(submissions, 'federalSpendToDate'),
    [submissions]
  );
  const latestMatch = useMemo(
    () => latestFieldValue(submissions, 'matchSpendToDate'),
    [submissions]
  );

  const paperSx: FinancialsPaperSx = (theme) => ({
    border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
    boxShadow:
      theme.colorScheme === 'dark'
        ? '0 0 12px rgba(255, 255, 255, 0.1)'
        : theme.shadows.md,
    '@media (max-width: 768px)': {
      margin: theme.spacing.sm,
    },
  });

  const asOfLabel = (submission: FinancialMonthSubmission) =>
    dayjs(`${submission.year}-${String(submission.month).padStart(2, '0')}-01`).format(
      'MMMM YYYY'
    );

  const allTimeFederal = latestFederal?.value ?? null;
  const allTimeMatch = latestMatch?.value ?? null;
  const allTimeFederalAsOf = latestFederal ? asOfLabel(latestFederal.submission) : null;
  const allTimeMatchAsOf = latestMatch ? asOfLabel(latestMatch.submission) : null;

  const awardStart = dayjs(awardWindow.startDate).startOf('day');
  const awardEnd = dayjs(awardWindow.endDate).startOf('day');
  const today = dayjs().startOf('day');
  const awardTotalDays = Math.max(awardEnd.diff(awardStart, 'day'), 1);
  const awardElapsedDays = Math.min(
    Math.max(today.diff(awardStart, 'day'), 0),
    awardTotalDays
  );
  const awardElapsedPct = Math.round((awardElapsedDays / awardTotalDays) * 100);
  const awardEnded = today.isAfter(awardEnd, 'day');
  const awardNotStarted = today.isBefore(awardStart, 'day');
  const awardMonthsLeft = Math.max(
    awardEnd.startOf('month').diff(dayjs().startOf('month'), 'month'),
    0
  );
  const awardDaysLeft = Math.max(awardEnd.diff(today, 'day'), 0);
  const awardTimeLeftLabel = awardNotStarted
    ? 'Not started'
    : awardEnded
      ? 'Ended'
      : awardMonthsLeft === 0
        ? `${awardDaysLeft} day${awardDaysLeft === 1 ? '' : 's'} left`
        : `${awardMonthsLeft} month${awardMonthsLeft === 1 ? '' : 's'} left`;

  const closeEditor = () => {
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setEditingKey(null);
    setFederalSpend('');
    setMatchSpend('');
  };

  const saveDraft = async (
    key: string,
    federal: number | '',
    match: number | ''
  ): Promise<boolean> => {
    const federalValue = federal === '' ? null : Number(federal);
    const matchValue = match === '' ? null : Number(match);

    if (federalValue !== null && federalValue < 0) return false;
    if (matchValue !== null && matchValue < 0) return false;

    const existing = submissions[key];
    const [yearPart, monthPart] = key.split('-');
    const year = Number(yearPart);
    const month = Number(monthPart);
    const label = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).format('MMMM YYYY');

    if (federalValue === null && matchValue === null && !existing) {
      return false;
    }

    if (
      existing &&
      existing.federalSpendToDate === federalValue &&
      existing.matchSpendToDate === matchValue
    ) {
      return false;
    }

    try {
      await upsertMutation.mutateAsync({
        year,
        month,
        federalSpendToDate: federalValue,
        matchSpendToDate: matchValue,
      });
      showSuccessNotification(
        federalValue === null && matchValue === null
          ? `Cleared spend for ${label}`
          : `Saved spend for ${label}`,
        'Financials saved'
      );
      return true;
    } catch {
      return false;
    }
  };

  const commitOrCancel = async () => {
    const draft = editDraftRef.current;
    if (!draft.editingKey) return;
    await saveDraft(draft.editingKey, draft.federalSpend, draft.matchSpend);
    closeEditor();
  };

  const openEditor = (row: FinancialMonthRow, field: EditableField) => {
    if (row.status === 'upcoming') return;
    if (upsertMutation.isPending) return;

    const nextKey = monthKey(row.year, row.month);

    if (editingKey === nextKey) {
      setFocusField(field);
      return;
    }

    if (editingKey && editingKey !== nextKey) {
      void commitOrCancel();
    }

    setFocusField(field);
    setEditingKey(nextKey);
    setFederalSpend(row.federalSpendToDate ?? '');
    setMatchSpend(row.matchSpendToDate ?? '');
  };

  const handleYearChange = (value: string | null) => {
    if (!value) return;
    closeEditor();
    setSelectedYear(value);
    const nextRows =
      value === 'all'
        ? statusFilter === 'all'
          ? allMonthRows
          : allMonthRows.filter((row) => row.status === statusFilter)
        : allMonthRows.filter((row) => {
            const matchesYear = row.year === Number(value);
            const matchesStatus = statusFilter === 'all' || row.status === statusFilter;
            return matchesYear && matchesStatus;
          });
    setPage(pageForCurrentWork(nextRows));
  };

  const handleStatusFilterChange = (value: string | null) => {
    const nextFilter = value ?? 'all';
    closeEditor();
    setStatusFilter(nextFilter);
    const nextRows = allMonthRows.filter((row) => {
      const matchesYear = selectedYear === 'all' || row.year === Number(selectedYear);
      const matchesStatus = nextFilter === 'all' || row.status === nextFilter;
      return matchesYear && matchesStatus;
    });
    setPage(pageForCurrentWork(nextRows));
  };

  const handlePageChange = (nextPage: number) => {
    closeEditor();
    setPage(nextPage);
  };

  const handleEditorKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commitOrCancel();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeEditor();
    }
  };

  const handleEditorBlur = (rowKey: string) => {
    blurTimeoutRef.current = window.setTimeout(() => {
      const active = document.activeElement;
      const rowEl = document.querySelector(`tr[data-row-key="${rowKey}"]`);
      if (rowEl && active instanceof Node && rowEl.contains(active)) {
        return;
      }
      void commitOrCancel();
    }, 0);
  };

  const handleEditorFocus = () => {
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const editableCellSx = (theme: any, editable: boolean) => ({
    display: 'block',
    width: '100%',
    minHeight: 36,
    padding: '6px 8px',
    borderRadius: theme.radius.sm,
    textAlign: 'right' as const,
    color: 'inherit',
    cursor: editable ? 'text' : 'default',
    border: '1px solid transparent',
    boxSizing: 'border-box' as const,
    '&:hover': editable
      ? {
          backgroundColor:
            theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
          borderColor:
            theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[3],
        }
      : undefined,
    '&:focus': editable
      ? {
          outline: `2px solid ${theme.colors.blue[5]}`,
          outlineOffset: 1,
        }
      : undefined,
  });

  const amountInputStyles = {
    root: { width: '100%' },
    wrapper: { width: '100%' },
    input: {
      minHeight: 36,
      height: 36,
      paddingTop: 6,
      paddingBottom: 6,
      paddingLeft: 8,
      paddingRight: 8,
      textAlign: 'right' as const,
    },
  };

  const renderLedgerRow = (row: FinancialMonthRow) => {
    const rowKey = monthKey(row.year, row.month);
    const isUpcoming = row.status === 'upcoming';
    const isReported = row.status === 'reported';
    const isEditing = editingKey === rowKey;
    const canEdit = !isUpcoming;

    return (
      <tr
        key={rowKey}
        data-row-key={rowKey}
        data-editing={isEditing ? 'true' : undefined}
        style={{ opacity: isUpcoming ? 0.65 : 1 }}
        onFocusCapture={handleEditorFocus}
        onBlurCapture={() => {
          if (isEditing) handleEditorBlur(rowKey);
        }}
      >
        <td>
          <Text size="sm" weight={500}>
            {row.label}
          </Text>
          {isEditing ? (
            <Text size="xs" color="dimmed">
              Enter to save, Esc to cancel.
            </Text>
          ) : null}
        </td>
        <td>
          {isEditing ? (
            <NumberInput
              value={federalSpend}
              onChange={setFederalSpend}
              min={0}
              precision={2}
              hideControls
              formatter={currencyFormatter}
              parser={currencyParser}
              size="sm"
              disabled={upsertMutation.isPending}
              onKeyDown={handleEditorKeyDown}
              aria-label={`Federal spend to date for ${row.label}`}
              styles={amountInputStyles}
            />
          ) : (
            <UnstyledButton
              onClick={() => openEditor(row, 'federal')}
              disabled={!canEdit}
              aria-label={
                row.federalSpendToDate !== null
                  ? `Edit federal spend for ${row.label}`
                  : `Enter federal spend for ${row.label}`
              }
              sx={(theme) => editableCellSx(theme, canEdit)}
            >
              <Text
                size="sm"
                weight={600}
                ta="right"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {row.federalSpendToDate !== null
                  ? formatCurrency(row.federalSpendToDate)
                  : '—'}
              </Text>
            </UnstyledButton>
          )}
        </td>
        <td>
          {isEditing ? (
            <NumberInput
              value={matchSpend}
              onChange={setMatchSpend}
              min={0}
              precision={2}
              hideControls
              formatter={currencyFormatter}
              parser={currencyParser}
              size="sm"
              disabled={upsertMutation.isPending}
              onKeyDown={handleEditorKeyDown}
              aria-label={`Match spend to date for ${row.label}`}
              styles={amountInputStyles}
            />
          ) : (
            <UnstyledButton
              onClick={() => openEditor(row, 'match')}
              disabled={!canEdit}
              aria-label={
                row.matchSpendToDate !== null
                  ? `Edit match spend for ${row.label}`
                  : `Enter match spend for ${row.label}`
              }
              sx={(theme) => editableCellSx(theme, canEdit)}
            >
              <Text
                size="sm"
                weight={600}
                ta="right"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {row.matchSpendToDate !== null
                  ? formatCurrency(row.matchSpendToDate)
                  : '—'}
              </Text>
            </UnstyledButton>
          )}
        </td>
        <td>
          {isReported && row.updatedAt ? (
            <Stack spacing={0}>
              <Text size="sm">
                {formatTimestampToLocal(row.updatedAt, 'MMM D, YYYY h:mm A')}
              </Text>
              {row.updatedBy ? (
                <Text size="xs" color="dimmed" truncate>
                  {row.updatedBy}
                </Text>
              ) : null}
            </Stack>
          ) : (
            <Text size="sm" color="dimmed">
              —
            </Text>
          )}
        </td>
      </tr>
    );
  };

  const rangeStart = filteredRows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filteredRows.length);

  if (isLoading) {
    if (embedded) {
      return <FinancialsPageSkeleton paperSx={paperSx} />;
    }
    return (
      <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
        <PageHeader title="Financials" subtitle={FINANCIALS_SUBTITLE} />
        <FinancialsPageSkeleton paperSx={paperSx} />
      </Container>
    );
  }

  if (error || !financials) {
    const errorAlert = (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mx={embedded ? 0 : 'sm'}>
        Failed to load financials. Please try again.
      </Alert>
    );
    if (embedded) {
      return errorAlert;
    }
    return (
      <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
        <PageHeader title="Financials" subtitle={FINANCIALS_SUBTITLE} />
        {errorAlert}
      </Container>
    );
  }

  const content = (
    <>
      <Stack spacing="md" mb="xl">
        <Paper p="md" shadow="md" radius="md" sx={paperSx}>
          <Stack spacing="md">
            <Stack spacing="sm">
              <div>
                <Text size="lg" weight={700}>
                  Spend Overview
                </Text>
                <Text size="sm" color="dimmed">
                  Cumulative spend vs award targets
                </Text>
              </div>

              <Stack spacing={6}>
                <Group position="apart" noWrap spacing="xs" align="center">
                  <Text size="xs" color="dimmed" sx={{ flex: 1 }}>
                    {awardStart.format('MMM D, YYYY')}
                  </Text>
                  <Text size="xs" weight={600} ta="center" sx={{ flexShrink: 0 }}>
                    {awardTimeLeftLabel}
                  </Text>
                  <Text size="xs" color="dimmed" ta="right" sx={{ flex: 1 }}>
                    {awardEnd.format('MMM D, YYYY')}
                  </Text>
                </Group>
                <Progress
                  value={awardNotStarted ? 0 : awardElapsedPct}
                  size="xs"
                  radius="xl"
                  color="gray"
                  aria-label={`Award window: ${awardTimeLeftLabel}`}
                />
              </Stack>
            </Stack>

            <SimpleGrid
              cols={2}
              spacing="md"
              breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
            >
              <SpendMetricTile
                eyebrow="All time"
                label="Federal spend"
                value={allTimeFederal}
                asOf={allTimeFederalAsOf}
                emptyLabel="No spend reported yet"
                target={targets.federalTarget}
              />
              <SpendMetricTile
                eyebrow="All time"
                label="Match spend"
                value={allTimeMatch}
                asOf={allTimeMatchAsOf}
                emptyLabel="No spend reported yet"
                target={targets.matchTarget}
              />
            </SimpleGrid>

            <GivebackRiskIndicator value={givebackRisk} />
          </Stack>
        </Paper>

        <Paper
          p="md"
          shadow="md"
          radius="md"
          sx={(theme) => ({
            ...paperSx(theme),
            maxWidth: 800,
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
              maxWidth: 'none',
            },
          })}
        >
          <Group
            spacing="md"
            sx={(theme) => ({
              '@media (max-width: 768px)': {
                flexDirection: 'column',
                alignItems: 'stretch',
              },
            })}
          >
            <Select
              label="Filter by Year"
              placeholder="Select year"
              value={selectedYear}
              onChange={handleYearChange}
              data={yearOptions}
              style={{ flex: 1, minWidth: 200 }}
              styles={{
                label: { marginBottom: 8 },
                root: {
                  '@media (max-width: 768px)': {
                    width: '100%',
                    minWidth: 'auto',
                  },
                },
              }}
            />
            <Select
              label="Filter by Month"
              placeholder="Select months to show"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              data={statusOptions}
              style={{ flex: 1, minWidth: 200 }}
              styles={{
                label: { marginBottom: 8 },
                root: {
                  '@media (max-width: 768px)': {
                    width: '100%',
                    minWidth: 'auto',
                  },
                },
              }}
            />
          </Group>
        </Paper>
      </Stack>

      <Paper
        shadow="md"
        radius="md"
        sx={(theme) => ({
          ...paperSx(theme),
          overflow: 'hidden',
        })}
      >
        <Box
          p="md"
          sx={(theme) => ({
            borderBottom: `1px solid ${
              theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]
            }`,
          })}
        >
          <Text size="lg" weight={700}>
            Monthly Ledger
          </Text>
          <Text size="sm" color="dimmed">
            Cumulative spend by reporting period
          </Text>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table
            horizontalSpacing="md"
            verticalSpacing="sm"
            fontSize="sm"
            sx={(theme) => ({
              tableLayout: 'fixed',
              width: '100%',
              minWidth: 640,
              'th, td': {
                overflow: 'hidden',
                verticalAlign: 'middle',
              },
              'th:nth-of-type(1), td:nth-of-type(1)': {
                width: '20%',
              },
              'th:nth-of-type(2), td:nth-of-type(2)': {
                width: '22%',
              },
              'td:nth-of-type(2)': {
                textAlign: 'right',
              },
              'th:nth-of-type(3), td:nth-of-type(3)': {
                width: '22%',
              },
              'td:nth-of-type(3)': {
                textAlign: 'right',
              },
              'th:nth-of-type(4), td:nth-of-type(4)': {
                width: '36%',
              },
              thead: {
                backgroundColor:
                  theme.colorScheme === 'dark'
                    ? theme.colors.dark[6]
                    : theme.colors.gray[0],
              },
              'thead th': {
                color: theme.colors.gray[6],
                fontWeight: 600,
                fontSize: theme.fontSizes.xs,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
              },
              'tbody tr[data-editing="true"]': {
                backgroundColor:
                  theme.colorScheme === 'dark'
                    ? theme.fn.rgba(theme.colors.blue[9], 0.2)
                    : theme.fn.rgba(theme.colors.blue[0], 0.9),
              },
            })}
          >
            <thead>
              <tr>
                <th>
                  <AppTooltip
                    label="Month-end this cumulative spend total is current through."
                    inline
                  >
                    <span style={{ cursor: 'help' }}>Month end</span>
                  </AppTooltip>
                </th>
                <th>
                  <AppTooltip
                    label="Cumulative federal spend to date for all time through this month."
                    inline
                  >
                    <span style={{ cursor: 'help' }}>Total Federal Spend</span>
                  </AppTooltip>
                </th>
                <th>
                  <AppTooltip
                    label="Cumulative match spend to date for all time through this month."
                    inline
                  >
                    <span style={{ cursor: 'help' }}>Total Match Spend</span>
                  </AppTooltip>
                </th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <Text size="sm" color="dimmed" align="center" py="xl">
                      No months match the selected filter.
                    </Text>
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => renderLedgerRow(row))
              )}
            </tbody>
          </Table>
        </Box>

        {totalPages > 1 ? (
          <Box
            p="md"
            sx={(theme) => ({
              borderTop: `1px solid ${
                theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]
              }`,
            })}
          >
            <PaginationBar
              page={safePage}
              totalPages={totalPages}
              totalItems={filteredRows.length}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onChange={handlePageChange}
              mt={0}
              pt={0}
            />
          </Box>
        ) : null}
      </Paper>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
      <PageHeader title="Financials" subtitle={FINANCIALS_SUBTITLE} />
      {content}
    </Container>
  );
}
